<template>
  <div
    class="product-options"
    :class="{
      [$class('loading')]: loading,
      'product-options__drawer': isDrawer && isProductFormDrawer,
    }"
  >
    <template v-if="isDrawer && isProductFormDrawer">
      <div
        v-for="(option) of options"
        :key="getOptionKey(option)"
        class="product-options__option"
        :class="{ [$class('hidden')]: !getDisplayOption(option) }"
      >
        <fieldset v-if="option.name === 'Size'">
          <legend
            v-if="displayOptionName"
            class="product-options__option-title text-body-m-regular visually-hidden"
            v-text="getOptionLabel(option)"
          />

          <div
            class="product-options__values--size"
          >
            <template
              v-for="(value, index) of option.values"
              :key="getOptionValueKey(value, index, option)"
            >
              <input
                :name="option.name"
                :value="selectedVariant"
                type="hidden"
              >

              <button
                :aria-controls="option.disabled[value] ? 'notify-me-drawer' : null"
                :aria-expanded="option.disabled[value] ? outOfStockExpanded : null"
                class="
                  product-options__value
                  product-options__value--button
                  product-options__value--size
                "
                :class="{
                  'product-options__value--out-of-stock': option.disabled[value],
                }"
                :disabled="getValueDisabled(option.disabled[value], 'disabled')"
                type="submit"
                @click.prevent="handleAddSizeToCart(option.disabled[value], value, option.index)"
              >
                <span class="text-body-s-regular product-options__label">
                  <span
                    aria-hidden="true"
                    v-text="value"
                  />

                  <span
                    v-if="option.isLowStock[value]"
                    class="product-options__stock-message product-options__stock-message--low"
                    v-text="$string('product.form.low_stock_warning_mobile')"
                  />

                  <span
                    v-if="option.disabled[value]"
                    class="product-options__stock-message"
                    v-text="$string('product.form.sold_out')"
                  />
                </span>

                <span
                  class="visually-hidden"
                  v-text="$string('product.form.add_size_to_cart').replace('{{ size }}', value)"
                />
              </button>
            </template>
          </div>

          <btn
            v-if="showSizeGuideBtn"
            aria-controls="size-guide-drawer"
            :aria-expanded="isSizeGuideExpanded"
            class="product-options__cta"
            :label="$string('product.form.size_guide')"
            modifiers="text"
            show-icon
            type="button"
            @click="handleOpenSizeGuide"
          >
            <template #icon>
              <icon-chevron-right-three />
            </template>
          </btn>
        </fieldset>
      </div>
    </template>

    <template v-else>
      <fieldset
        v-for="(option) of options"
        :key="getOptionKey(option)"
        class="product-options__option"
        :class="{ [$class('hidden')]: !getDisplayOption(option) }"
      >
        <template v-if="option.name === 'Color'">
          <legend
            v-if="displayOptionName"
            class="product-options__option-title text-body-m-regular"
            v-text="getOptionLabel(option)"
          />

          <color-swatches
            v-if="colorOptions && !hideColors"
            class="product-options__values product-options__values--color"
            :color-options="colorOptions"
            :disabled-codes="disabledColorCodes"
            :featured-image-src="filteredMediaImage?.src || product.featuredImage?.src"
            :input-name="getOptionKey(option)"
            :namespace="namespace"
            :product-handle="product.handle"
            :variants="product.variants"
            @update-active-swatch="handleActiveSwatch"
          />
        </template>

        <template v-else-if="option.name === 'Size' && showSizeOnMobile">
          <legend
            v-if="displayOptionName"
            class="product-options__option-title text-body-m-regular"
            :class="{[$class('hidden')]: hideLabels }"
            v-text="getOptionLabel(option)"
          />

          <div
            class="product-options__values"
            :class="{ 'product-options__values--size': option.name === 'Size' }"
          >
            <selection-tab
              v-for="(value, index) of option.values"
              :id="getOptionValueKey(value, index, option)"
              :key="getOptionValueKey(value, index, option)"
              v-model="currentOptions[option.index]"
              class="product-options__value"
              :class="{
                'product-options__value--out-of-stock': option.disabled[value],
                'product-options__value--size': option.name === 'Size'
              }"
              :disabled="getValueDisabled(option.disabled[value], 'disabled')"
              :label="option.labels[index]"
              label-class="product-options__label"
              :name="getOptionKey(option)"
              :value="value"
              :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
            />
          </div>

          <btn
            v-if="showSizeGuideBtn"
            aria-controls="size-guide-drawer"
            :aria-expanded="isSizeGuideExpanded"
            class="product-options__cta"
            :label="$string('product.form.size_guide')"
            modifiers="text"
            show-icon
            type="button"
            @click="handleOpenSizeGuide"
          >
            <template #icon>
              <icon-chevron-right-three />
            </template>
          </btn>
        </template>

        <template v-else-if="product.giftCard">
          <legend
            v-if="displayOptionName"
            class="product-options__option-title text-body-m-regular"
            :class="{[$class('hidden')]: hideLabels }"
            v-text="getOptionLabel(option)"
          />

          <div
            class="product-options__values"
          >
            <selection-tab
              v-for="(value, index) of option.values"
              :id="getOptionValueKey(value, index, option)"
              :key="getOptionValueKey(value, index, option)"
              v-model="currentOptions[option.index]"
              :model-value="variant.title"
              class="product-options__value"
              :class="{
                'product-options__value--out-of-stock': option.disabled[value],
              }"
              :disabled="getValueDisabled(option.disabled[value], 'disabled')"
              :label="option.labels[index]"
              label-class="product-options__label"
              :name="getOptionKey(option)"
              :value="value"
              :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
            />
          </div>
        </template>
      </fieldset>
    </template>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select"
      name="id"
      aria-hidden="true"
    >
      <option
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
        :value="variantObject.id"
        v-text="`${variantObject.title} - ${variantObject.id}`"
      />
    </select>
  </div>
</template>

<script>

/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Array} colorOptions - All color options for all matched products.
 * @param {Object} drawerColorSwatch - Color swatch for product options in
 * drawer.
 * @param {Object} filteredMediaImage - First image in media array.
 * @param {Boolean} hasSizeGuide - Whether Liquid passed size guide data.
 * @param {Boolean} [hideColors] - Hide color swatches from render.
 * @param {Boolean} [hideLabels] - Hide additional labels from option.
 * @param {Boolean} [isDrawer] - If instantiation is in a drawer.
 * @param {Boolean} isSizeGuideExpanded - If size guide drawer is expanded.
 * @param {Boolean} [loading] - Loading state.
 * @param {String} namespace - Namespace.
 * @param {Boolean} outOfStockExpanded - If the notify me drawer is open.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean} [showSizeMobile] - Show size options for mobile resolutions.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapState } from 'vuex'

import ColorSwatches from '~async/color-swatches/color-swatches'
import SelectionTab from '~async/utils/selection-tab/selection-tab'
import Btn from '~global/btn/btn'
import IconChevronRightThree from '~icons/directional-navigation/chevron/right-3.svg'

import { convertToHandle } from '~/helpers/convert'
import { getDisabledColorCodes } from '~/helpers/product'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductOptions',

  components: {
    Btn,
    ColorSwatches,
    IconChevronRightThree,
    SelectionTab,
  },

  props: {
    colorOptions: {
      type: Array,
      default: () => [],
    },
    drawerColorSwatch: {
      type: Object,
      default: () => ({
        code: '',
        label: '',
        value: '',
      }),
    },
    filteredMediaImage: {
      type: Object,
      default: () => ({}),
    },
    hasSizeGuide: {
      type: Boolean,
      default: false,
    },
    hideColors: {
      type: Boolean,
      default: false,
    },
    hideLabels: {
      type: Boolean,
      default: false,
    },
    isDrawer: {
      type: Boolean,
      default: false,
    },
    isProductFormDrawer: {
      type: Boolean,
      default: false,
    },
    isOneSize: {
      type: Boolean,
      default: false,
    },
    isSizeGuideExpanded: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    outOfStockExpanded: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        hideGiftCardOptionNames: true,
        id: '',
      }),
      required: true,
    },
    showSizeMobile: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'add-to-cart': (payload) => payload === null,
    'open-size-guide': (payload) => typeof payload === 'boolean',
    'open-notify-me-drawer': (payload) => typeof payload === 'boolean',
    'update-active-swatch': (payload) => typeof payload === 'object',
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      colorIndex: 0,
      colorLabel: '',
      currentVariantId: 0,
      currentOptions: [],
      lowStockThreshold: this.$variable('settings.product.lowStockThreshold'),
      options: [],
      selectedVariant: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Get array of disabled color codes.
     * If product has style group tag with valid swatch products,
     * those products can re-enable color codes that they have enabled.
     * @returns {Array}
     */
    disabledColorCodes() {
      return getDisabledColorCodes(this.product.tags)

    },

    /**
     * Compute if option name should be displayed.
     * @returns {Boolean}
     */
    displayOptionName() {
      return !this.product.giftCard
    },

    /**
     * Determine whether to display the size guide button.
     * @returns {Boolean}
     */
    showSizeGuideBtn() {
      return !this.hideLabels && this.hasSizeGuide
    },

    /**
     * Determine whether to show size input options.
     * @returns {Boolean}
     */
    showSizeOnMobile() {
      return this.showSizeMobile ? !this.isOneSize : !this.isMobile
    },
  },

  watch: {

    /**
     * Watch current options to update based on option choices.
     */
    currentOptions: {
      handler() {
        this.getOptions()
        this.setSeasonOption()
        const variantTitle = this.currentOptions.join(' / ')
        const variant = this.getVariantFromProperty(variantTitle, 'title')

        if (!variant) {
          return
        }

        this.currentVariantId = variant.id
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromProperty(this.currentVariantId)

      if (!variant) {
        return
      }

      this.selectedVariant = variant

      this.$emit('update-variant', variant)
    },

    /**
     * Watch drawerColorSwatch when component is rendered in product form drawer
     * to ensure that current options updates with colour choice.
     */
    drawerColorSwatch() {
      if (!this.isDrawer) {
        return
      }

      this.setDrawerColorChoice()
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     * @param {Object} newProduct - New product object.
     * @param {Object} oldProduct - Old product object.
     */
    product(newProduct, oldProduct) {
      if (!this.styleGroupTag) {
        return
      }

      /**
       * If new and old product ids match, return to prevent saved current
       * options (e.g. colour & season) from being overwritten.
       */
      if (newProduct.id === oldProduct.id) {
        return
      }

      this.currentOptions = [...Array(this.product.options.length)]
      this.getCurrentVariant()
    },

    /**
     * Watch variant for updates to update current options.
     */
    variant() {
      if (!this.variant) {
        this.currentVariantId = 0
      }

      this.getCurrentOptions()
    },
  },

  mounted() {
    this.getCurrentVariant()
    this.getOptions()
    this.setDrawerColorChoice()
  },

  methods: {

    /**
     * Get if option should be rendered.
     * @param {Object} option - Option object.
     * @returns {Boolean}
     */
    getDisplayOption(option) {
      if (option.name.toLowerCase().includes('season')) {
        return false
      }

      if (option.name === 'Size') {
        return !this.isOneSize &&
          (this.showSizeOnMobile || (this.isDrawer && this.isMobile))
      }

      if (option.name === 'Color') {
        return !this.hideColors
      }

      return true
    },

    /**
     * Get option key.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionKey(option) {
      return `${this.section.id}-${this.product.handle}-option-${option.index}-${convertToHandle(option.name)}`
    },

    /**
     * Get option label. Include color/size label if color/size option.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionLabel(option) {
      let optionName = option.name

      if (option.name === 'Color' && this.colorLabel) {
        optionName = `${option.name}: ${this.colorLabel}`
      }

      if (option.name === 'Size') {
        optionName = this.currentOptions[option.index]
          ? `${option.name}: ${this.currentOptions[option.index]}`
          : `${option.name}:`
      }

      return optionName
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @param {Number} index - Forloop index.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionValueKey(value, index, option) {
      return `${this.namespace}-${this.getOptionKey(option)}-value-${index}-${convertToHandle(value)}`
    },

    /**
     * Get variant key.
     * @param {Object} variant - Variant object.
     * @param {Number} index - Forloop index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product.handle}-option-${index}-${convertToHandle(variant.title)}`
    },

    /**
     * Get if value is disabled.
     * - Based on section setting and attribute.
     * @param {Boolean} available - Value availability.
     * @param {String} attribute - Selection tab attribute.
     * @returns {Boolean}
     */
    getValueDisabled(available, attribute) {
      if (!available) {
        return false
      }

      return attribute === 'visually-disabled'
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (this.$variable('storybook') && this.variant) {
        this.getCurrentOptions()
        return
      }

      /**
       * Always get current variant from URL.
       */
      const variantFromUrl = this.getVariantFromUrl()

      this.setOneSize()

      if (!variantFromUrl) {
        return
      }

      if (!this.product.giftCard) {
        this.setActiveColorFromVariant(variantFromUrl)
      }

      this.currentVariantId = variantFromUrl.id
      this.getCurrentOptions()
    },

    /**
     * Prevent PDP from defaulting to a different variant on page load.
     * - Build color data from variant.selectedOptions, and emit this data.
     * @param {Object} currentVariant - Current variant from URL.
     */
    setActiveColorFromVariant(currentVariant) {
      if (!currentVariant) {
        return
      }

      const selectedColor = currentVariant.selectedOptions.find((option) => {
        return option.name === 'Color'
      })

      const colorCodeLabel = selectedColor.value.split('-')

      const selectedColorData = {
        code: colorCodeLabel[0].trim(),
        label: colorCodeLabel[1].trim(),
        value: selectedColor.value,
      }

      this.handleActiveSwatch(selectedColorData)
    },

    /**
     * Set current season based on color & size selection.
     */
    setSeasonOption() {
      if (!this.currentOptions[this.colorIndex] || this.product.giftCard) {
        return
      }

      const seasonIndex = this.product.options.findIndex((option) => {
        return option.name.toLowerCase().includes('season')
      })

      const matchingVariant = this.product.variants.find((variant) => {
        return variant.title.includes(this.currentOptions[this.colorIndex])
      })

      this.currentOptions[seasonIndex] = matchingVariant.selectedOptions[seasonIndex].value
    },

    /**
     * Set one size value in this.currentOptions.
     */
    setOneSize() {
      if (!this.isOneSize) {
        return
      }

      const oneSize = this.product.options.find((option) => {
        return option.name === 'Size'
      })

      if (!oneSize) {
        return
      }

      this.currentOptions[oneSize.index] = oneSize.values[0]
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromProperty(variantId)
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object}
     */
    getVariantFromProperty(value, property = 'id') {
      return this.product.variants?.find((variant) => {
        return variant[property] === value
      })
    },

    /**
     * Get current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        this.currentOptions = [...Array(this.product.options.length)]
        return
      }

      this.variant.selectedOptions.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value
      })
    },

    /**
     * Get extended options data.
     * - Extends product data with disabled data.
     * @returns {Array}
     */
    getOptions() {
      if (!this.product?.options?.length) {
        this.options = []
        return
      }

      this.options = this.product.options.map((option, optionIndex) => {
        const disabled = {}
        const isLowStock = {}

        /**
         * Compute if option is unavailable and low stock.
         * - First find variants that match current value.
         * - Then find variants that match other currentOptions.
         * - Then test to see if all matching variants are unavailable or low
         *   stock.
         */
        option.values.forEach((value) => {
          const filteredVariants = this.product.variants
            .filter((variant) => variant.selectedOptions[optionIndex].value === value)
            .filter((variant) => {
              return this.currentOptions.every((currentOption, currentOptionIndex) => {
                if (!currentOption || optionIndex === currentOptionIndex) {
                  return true
                }

                return variant.selectedOptions[currentOptionIndex]?.value === currentOption
              })
            })

          disabled[value] = filteredVariants.every((variant) => !variant.available)

          isLowStock[value] = filteredVariants
            .every((variant) => {
              return variant.inventory < this.lowStockThreshold &&
                variant.inventory > 0
            })
        })

        /**
         * Return extended data.
         */
        return {
          ...option,
          disabled,
          isLowStock,
        }
      })
    },

    /**
     * Handle events for updating the active swatch.
     * @param {Object} colorData - Selected color data.
     */
    handleActiveSwatch(colorData) {
      this.colorLabel = colorData.label
      this.currentOptions[this.colorIndex] = colorData.value

      this.$emit('update-active-swatch', colorData)
    },

    /**
     * Add selected size to cart.
     * - Used when component is instantiated in product form drawer component,
     *   as the size selection doubles as the 'add to cart' button.
     * @param {String} value - Size value.
     * @param {Number} index - Index of size in options array.
     */
    addSizeToCart(value, index) {
      this.currentOptions[index] = value

      this.$nextTick().then(() => {
        this.$emit('add-to-cart', null)
      })
    },

    /**
     * If in product form drawer component, set the active colour based on
     * decision from product form on page.
     */
    setDrawerColorChoice() {
      if (!this.currentOptions[this.colorIndex] && this.product.giftCard) {
        return
      }

      this.currentOptions[this.colorIndex] = this.drawerColorSwatch.value
    },

    /**
     * Emit event to open the size guide drawer.
     */
    handleOpenSizeGuide() {
      this.$emit('open-size-guide', true)
    },

    /**
     * Handle events when a product form drawer size is selected.
     * - Either emit event to open notify me drawer or add product to cart.
     * @param {Boolean} isOutOfStock - If product is out of stock.
     * @param {String} value - Size value.
     * @param {Number} index - Index of size in options array.
     */
    handleAddSizeToCart(isOutOfStock, value, index) {
      if (isOutOfStock) {
        this.$emit('open-notify-me-drawer', true)
        return
      }

      this.addSizeToCart(value, index)
    },
  },
}
</script>

<style lang="scss">
@import './product-options';
</style>
